/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops UI の小さな表示プリミティブ群（SLA チップ・ステート/緊急度/種別（カテゴリ）/分類バッジ・ラベル付き行）。デザインシステムの Badge にドメインの色マッピングを与えて demo.html の見た目を PIL トークンで再現する
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { OPS_WORK_MINUTES_OPTIONS, opsWorkMinutesSuggestion } from '@cortex/ops-core';
import type { SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { Badge, type BadgeProps } from '@cortex/web-design-system/components/ui/Badge';
import type {
  OpsCategory,
  OpsClassification,
  OpsSla,
  OpsState,
  OpsUrgency,
  SlaState,
  SlaStatusKind,
} from '../../../types/ops.js';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/** SLA 状態 → Badge バリアント。 @graph-connects none */
const SLA_VARIANT: Record<SlaState, BadgeVariant> = {
  ok: 'success',
  warn: 'warning',
  breach: 'error',
  none: 'default',
};

/** ステート → Badge バリアント。 @graph-connects none */
const STATE_VARIANT: Record<OpsState, BadgeVariant> = {
  open: 'info',
  investigating: 'warning',
  blocked: 'error',
  closed: 'default',
};

/** ステート → 翻訳キー（module scope では hook を呼べないため t() で解決する）。 @graph-connects none */
const STATE_LABEL_KEY: Record<OpsState, string> = {
  open: 'ops.common.state.open',
  investigating: 'ops.common.state.investigating',
  blocked: 'ops.common.state.blocked',
  closed: 'ops.common.state.closed',
};

/** 緊急度 → Badge バリアント。 @graph-connects none */
const URGENCY_VARIANT: Record<OpsUrgency, BadgeVariant> = {
  即時: 'error',
  当日: 'warning',
  通常: 'default',
};

/** 緊急度（生の enum）→ 翻訳キー。raw enum を描画せずキー経由で表示する。 @graph-connects none */
const URGENCY_LABEL_KEY: Record<OpsUrgency, string> = {
  即時: 'ops.common.urgency.immediate',
  当日: 'ops.common.urgency.sameDay',
  通常: 'ops.common.urgency.standard',
};

/** ops カテゴリ enum → 翻訳キー（ops シートの 4 カテゴリのみ）。 @graph-connects none */
const CATEGORY_LABEL_KEY: Partial<Record<OpsCategory, string>> = {
  調査依頼: 'ops.common.category.investigation',
  状態変更: 'ops.common.category.stateChange',
  データ修正: 'ops.common.category.dataFix',
  SQLレビュー: 'ops.common.category.sqlReview',
};

/** カテゴリ enum を現在の言語で表示する。未知のカテゴリ値は生の値をそのまま返す。 @graph-connects none */
export function opsCategoryLabel(category: OpsCategory, t: TFunction): string {
  const key = CATEGORY_LABEL_KEY[category];
  return key === undefined ? category : t(key);
}

/**
 * ops カテゴリ → Badge バリアント。「本番データに触るか」の軸で色を割り当てる
 * （読み取りのみ=info / 状態を書き換える=warning / データを書き換える=error / レビュー=success）。
 * @graph-connects none
 */
const CATEGORY_VARIANT: Partial<Record<OpsCategory, BadgeVariant>> = {
  調査依頼: 'info',
  状態変更: 'warning',
  データ修正: 'error',
  SQLレビュー: 'success',
};

/** CategoryBadge の props。 */
export interface CategoryBadgeProps {
  /** 依頼カテゴリ。 */
  category: OpsCategory;
  /** バッジのサイズ（既定 sm）。詳細ヘッダのように目立たせたい場所は md を使う。 */
  size?: 'sm' | 'md';
}

/** 依頼カテゴリをバッジ表示する。 @graph-connects none */
export function CategoryBadge({ category, size = 'sm' }: CategoryBadgeProps): ReactNode {
  const { t } = useTranslation();
  return (
    <Badge
      variant={CATEGORY_VARIANT[category] ?? 'default'}
      size={size}
      solid
      className="whitespace-nowrap"
    >
      {opsCategoryLabel(category, t)}
    </Badge>
  );
}

/** 分類 → Badge バリアント。 @graph-connects none */
const CLASSIFICATION_VARIANT: Record<OpsClassification, BadgeVariant> = {
  bug: 'error',
  spec: 'info',
  data: 'warning',
  dashboard: 'info',
  user_error: 'default',
  unknown: 'default',
};

/** 分類 → 翻訳キー（module scope では hook を呼べないため t() で解決する）。 @graph-connects none */
const CLASSIFICATION_LABEL_KEY: Record<OpsClassification, string> = {
  bug: 'ops.common.classification.bug',
  spec: 'ops.common.classification.spec',
  data: 'ops.common.classification.data',
  dashboard: 'ops.common.classification.dashboard',
  user_error: 'ops.common.classification.user_error',
  unknown: 'ops.common.classification.unknown',
};

/** SLA ステータス種別 → 翻訳キー（残り営業日 / 超過営業日 / 当日締め）。 @graph-connects none */
const SLA_STATUS_LABEL_KEY: Record<SlaStatusKind, string> = {
  left: 'ops.common.sla.left',
  over: 'ops.common.sla.over',
  dueToday: 'ops.common.sla.dueToday',
  none: 'ops.common.sla.none',
};

/**
 * SLA の構造化ステータスを現在の言語のラベルへ変換する。ops-core は言語非依存の `status`（kind + days）を
 * 返すため、英語文字列（`sla.label`）を parse せずここで i18n する。
 * @graph-connects none
 */
export function slaStatusLabel(sla: OpsSla, t: TFunction): string {
  return t(SLA_STATUS_LABEL_KEY[sla.status.kind], { days: sla.status.days });
}

/** SlaChip の props。 */
export interface SlaChipProps {
  /** 算出済みの SLA。 */
  sla: OpsSla;
}

/** SLA の残り/超過を色付きチップで表示する。翻訳ラベルは折り返さない（円形バッジ内で改行させない）。 @graph-connects none */
export function SlaChip({ sla }: SlaChipProps): ReactNode {
  const { t } = useTranslation();
  if (sla.state === 'none') return null;
  return (
    <Badge variant={SLA_VARIANT[sla.state]} size="sm" solid className="whitespace-nowrap">
      {t('ops.common.slaChip', { label: slaStatusLabel(sla, t) })}
    </Badge>
  );
}

/** StateBadge の props。 */
export interface StateBadgeProps {
  /** 依頼ステート。 */
  state: OpsState;
}

/** 依頼ステートをバッジ表示する。 @graph-connects none */
export function StateBadge({ state }: StateBadgeProps): ReactNode {
  const { t } = useTranslation();
  return (
    <Badge variant={STATE_VARIANT[state]} size="sm" className="whitespace-nowrap">
      {t(STATE_LABEL_KEY[state])}
    </Badge>
  );
}

/** UrgencyBadge の props。 */
export interface UrgencyBadgeProps {
  /** 緊急度。 */
  urgency: OpsUrgency;
}

/** 緊急度をバッジ表示する。通常は控えめ。 @graph-connects none */
export function UrgencyBadge({ urgency }: UrgencyBadgeProps): ReactNode {
  const { t } = useTranslation();
  return (
    <Badge variant={URGENCY_VARIANT[urgency]} size="sm" className="whitespace-nowrap">
      {t(URGENCY_LABEL_KEY[urgency])}
    </Badge>
  );
}

/** ClassificationBadge の props。 */
export interface ClassificationBadgeProps {
  /** 分類（未分類は null）。 */
  classification: OpsClassification | null;
}

/** 調査後分類をバッジ表示する。未分類は「—」。 @graph-connects none */
export function ClassificationBadge({ classification }: ClassificationBadgeProps): ReactNode {
  const { t } = useTranslation();
  if (classification === null) {
    return <span className="text-[var(--color-text-muted)] text-sm">—</span>;
  }
  return (
    <Badge variant={CLASSIFICATION_VARIANT[classification]} size="sm" className="whitespace-nowrap">
      {t(CLASSIFICATION_LABEL_KEY[classification])}
    </Badge>
  );
}

/** InfoRow の props。 */
export interface InfoRowProps {
  /** 左のラベル。 */
  label: string;
  /** 右の値。 */
  children: ReactNode;
}

/** ラベル + 値の 1 行（AI コンテキスト等の小さな明細に使う）。 @graph-connects none */
export function InfoRow({ label, children }: InfoRowProps): ReactNode {
  return (
    <div className="flex items-baseline gap-3 py-0.5 text-sm">
      <span className="w-28 shrink-0 text-xs text-[var(--color-text-muted)]">{label}</span>
      <span className="min-w-0 truncate text-[var(--color-text-primary)]">{children}</span>
    </div>
  );
}

/**
 * 作業時間ドロップダウンの選択肢を組み立てる。先頭は常に「未記録」で、種別ごとの目安だけラベルを変える。
 *
 * クローズモーダルと工数カードの 2 箇所で同じものを出すため共有する（各所で組み立てると、
 * 片方だけ目安の出し方が変わる・片方だけ未記録の扱いが変わる、が静かに起きる）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 作業時間ドロップダウンの選択肢（未記録 + 分の一覧）を作り、種別ごとの目安にだけ目安ラベルを当てる
 * @graph-connects opsWorkMinutesSuggestion [calls] 種別ごとの目安（記録済み中央値）を取得
 */
export function opsWorkMinutesOptions(category: OpsCategory, t: TFunction): SelectOption[] {
  const suggestion = opsWorkMinutesSuggestion(category);
  return [
    { value: '', label: t('ops.detail.workMinutes.unrecorded') },
    ...OPS_WORK_MINUTES_OPTIONS.map((m) => ({
      value: String(m),
      label:
        m === suggestion
          ? t('ops.detail.workMinutes.optionSuggested', { count: m })
          : t('ops.detail.workMinutes.option', { count: m }),
    })),
  ];
}
